<script setup lang="ts">
// Homepage showreel (files and metadata in shared/utils/showreel.ts).
//
// The server renders only the poster (a CSS background swapped by the .dark
// class like AppLogo), so there is no flash, no hydration mismatch with the
// visitor's colour mode, and only one poster file downloads. The <video> mounts on the client and picks its file at runtime:
// - colour mode: the dark or the light film. Both share one timeline, so a mode
//   switch keeps the playhead and the play state.
// - size: the 720p H.264 file when the player needs under ~1000 device pixels
//   (phones), otherwise 1080p.
// - codec: 1080p AV1 only where MediaCapabilities reports smooth AND
//   power-efficient decoding (hardware AV1), otherwise 1080p H.264.
// - data: nothing loads until the page has loaded and the player is near the
//   viewport, and then only the file header (preload="metadata"); the film
//   itself streams once a quarter of the player is visible and it plays.
//   Save-Data, 2G and prefers-reduced-motion get a click-to-play poster (720p
//   for the data cases) instead of autoplay.
// Autoplay is muted (browser policy). It pauses off-screen and in hidden tabs.
// The first unmute restarts the film so the music lands on its "Click to
// Enter" moment.
import type { ShowreelMode } from '#shared/utils/showreel'

type Variant = 'av1' | 'mp4' | 'mp4Small'
type NetworkInfo = { saveData?: boolean, effectiveType?: string }

const config = useRuntimeConfig()
const colorMode = useColorMode()

const supabaseUrl = String(config.public.supabase?.url ?? '')
const fileUrl = (file: string) => showreelUrl(supabaseUrl, file)
const posters = {
  dark: fileUrl(SHOWREEL.files.dark.poster),
  light: fileUrl(SHOWREEL.files.light.poster)
}

const root = ref<HTMLElement | null>(null)
const video = ref<HTMLVideoElement | null>(null)

const mode = computed<ShowreelMode>(() => (colorMode.value === 'dark' ? 'dark' : 'light'))
const started = ref(false) // a source is attached
const variant = ref<Variant>('mp4')
const autoplay = ref(true)
const playing = ref(false)
const muted = ref(true)

const src = computed(() => (started.value ? fileUrl(SHOWREEL.files[mode.value][variant.value]) : undefined))

let lowData = false
let inView = false
let userPaused = false
let heardFromStart = false
let resumeAt: { time: number, play: boolean } | null = null

function network(): NetworkInfo | undefined {
  return (navigator as Navigator & { connection?: NetworkInfo }).connection
}

async function av1Smooth(): Promise<boolean> {
  if (!document.createElement('video').canPlayType(SHOWREEL.av1Type)) return false
  // Without MediaCapabilities we cannot tell hardware from software decoding:
  // H.264 is hardware-decoded everywhere, so prefer it.
  if (!('mediaCapabilities' in navigator)) return false
  try {
    const info = await navigator.mediaCapabilities.decodingInfo({
      type: 'file',
      video: { contentType: SHOWREEL.av1Type, width: SHOWREEL.width, height: SHOWREEL.height, bitrate: 1_400_000, framerate: 60 }
    })
    return info.supported && info.smooth && info.powerEfficient
  } catch {
    return false
  }
}

async function pickVariant(): Promise<Variant> {
  const cssWidth = root.value?.clientWidth || window.innerWidth
  const devicePixels = cssWidth * Math.min(window.devicePixelRatio || 1, 2)
  if (lowData || devicePixels < 1000) return 'mp4Small'
  return (await av1Smooth()) ? 'av1' : 'mp4'
}

async function attach() {
  if (started.value) return
  variant.value = await pickVariant()
  started.value = true
}

async function play() {
  const el = video.value
  if (!el) return
  try {
    await el.play()
  } catch {
    // Autoplay refused (e.g. low-power mode): fall back to click-to-play
    if (el.muted) autoplay.value = false
  }
}

function syncPlayback() {
  const el = video.value
  if (!el || !autoplay.value) return
  if (inView && !userPaused && !document.hidden) play()
  else el.pause()
}

function togglePlay() {
  const el = video.value
  if (!el) return
  if (el.paused) {
    userPaused = false
    play()
  } else {
    userPaused = true
    el.pause()
  }
}

function toggleSound() {
  const el = video.value
  if (!el) return
  if (el.muted) {
    if (!heardFromStart) {
      el.currentTime = 0
      heardFromStart = true
    }
    el.muted = false
    muted.value = false
    userPaused = false
    if (el.paused) play()
  } else {
    el.muted = true
    muted.value = true
  }
}

// Click-to-play (reduced motion, Save-Data, 2G, or autoplay refused): play
// with sound from the start, as the visitor asked for it. The file is picked
// synchronously (H.264 only) so play() stays inside the click's user
// activation window, which Safari requires for sound.
async function playWithSound() {
  autoplay.value = true
  muted.value = false
  heardFromStart = true
  if (!started.value) {
    const cssWidth = root.value?.clientWidth || window.innerWidth
    variant.value = lowData || cssWidth * Math.min(window.devicePixelRatio || 1, 2) < 1000 ? 'mp4Small' : 'mp4'
    started.value = true
  }
  await nextTick()
  const el = video.value
  if (!el) return
  el.muted = false
  el.currentTime = 0
  userPaused = false
  play()
}

function onLoadedMetadata() {
  const el = video.value
  if (!el) return
  if (resumeAt) {
    el.currentTime = Math.min(resumeAt.time, Math.max(0, el.duration - 0.05))
    if (resumeAt.play) play()
    resumeAt = null
  } else {
    syncPlayback()
  }
}

// Dark/light switch mid-play: same timeline, so continue where it was
watch(mode, () => {
  const el = video.value
  if (!el || !started.value) return
  resumeAt = { time: el.currentTime, play: !el.paused }
})

const onVisibility = () => syncPlayback()

onMounted(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const net = network()
  lowData = !!net?.saveData || /(^|-)2g$/.test(net?.effectiveType ?? '')
  autoplay.value = !reducedMotion && !lowData

  const el = root.value
  if (!el) return

  // Start buffering a little before the player scrolls into view, but never
  // before the page itself has finished loading.
  const near = new IntersectionObserver((entries) => {
    if (!autoplay.value || !entries.some(e => e.isIntersecting)) return
    near.disconnect()
    const go = () => attach()
    if (document.readyState === 'complete') go()
    else window.addEventListener('load', go, { once: true })
  }, { rootMargin: '400px 0px' })

  // Play only while at least a quarter of the player is visible
  const visible = new IntersectionObserver((entries) => {
    for (const e of entries) inView = e.isIntersecting && e.intersectionRatio >= 0.25
    syncPlayback()
  }, { threshold: [0, 0.25] })

  near.observe(el)
  visible.observe(el)
  document.addEventListener('visibilitychange', onVisibility)

  onUnmounted(() => {
    near.disconnect()
    visible.disconnect()
    document.removeEventListener('visibilitychange', onVisibility)
  })
})
</script>

<template>
  <figure
    ref="root"
    class="relative mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border border-default bg-muted shadow-(--surface-shadow)"
  >
    <div class="relative aspect-video">
      <!-- Poster: rendered on the server, picked by colour mode in CSS. A
      background (not two <img>s) so only the visible mode's file downloads. -->
      <div
        class="showreel-poster absolute inset-0 bg-cover bg-center"
        :style="{ '--poster-light': `url('${posters.light}')`, '--poster-dark': `url('${posters.dark}')` }"
      />

      <ClientOnly>
        <video
          v-if="started"
          ref="video"
          :src="src"
          :poster="posters[mode]"
          :muted="muted"
          :aria-label="SHOWREEL.name"
          playsinline
          loop
          preload="metadata"
          disableremoteplayback
          class="absolute inset-0 size-full object-cover"
          @loadedmetadata="onLoadedMetadata"
          @play="playing = true"
          @pause="playing = false"
        />

        <div
          v-if="!autoplay && !playing"
          class="absolute inset-0 flex items-center justify-center"
        >
          <UButton
            icon="i-lucide-play"
            size="xl"
            color="neutral"
            variant="solid"
            class="rounded-full p-4"
            :aria-label="`Play ${SHOWREEL.name} with sound`"
            @click="playWithSound"
          />
        </div>

        <div
          v-else-if="started"
          class="absolute bottom-3 right-3 flex items-center gap-2"
        >
          <UButton
            :icon="playing ? 'i-lucide-pause' : 'i-lucide-play'"
            size="sm"
            color="neutral"
            variant="solid"
            :aria-label="playing ? 'Pause video' : 'Play video'"
            @click="togglePlay"
          />
          <UButton
            :icon="muted ? 'i-lucide-volume-x' : 'i-lucide-volume-2'"
            :label="muted ? 'Sound on' : undefined"
            size="sm"
            color="neutral"
            variant="solid"
            :aria-label="muted ? 'Turn sound on' : 'Mute'"
            @click="toggleSound"
          />
        </div>
      </ClientOnly>
    </div>
    <figcaption class="sr-only">
      {{ SHOWREEL.description }}
    </figcaption>
  </figure>
</template>

<style scoped>
.showreel-poster {
  background-image: var(--poster-light);
}

.dark .showreel-poster {
  background-image: var(--poster-dark);
}
</style>
